/**
 * EscalationDrawer — Add/Edit an escalation within a workflow.
 *
 * Fields: name, PeriodTimeSlider, output channel toggles,
 * each with repeat interval and entity selector(s).
 *
 * Text/Voice/Email outputs are paired: the user output (4/5/6) and the
 * emergency contact output (17/18/19) share a single toggle and repeat
 * interval, but render two independent entity selector tables.
 *
 * Props:
 *   open              — drawer visibility
 *   onClose           — close callback
 *   onSave            — save callback (formData)
 *   onDelete          — delete callback (for edit mode)
 *   saving            — boolean, disables buttons while saving
 *   editTarget        — null for add, escalation object for edit
 *   escMax            — max escalation period in seconds (default 3600)
 *   userGroups        — array of user group records (for mobile/pager)
 *   users             — array of user records (for text/voice/email)
 *   emergencyContacts — array of emergency contact records
 *   campusSettings    — campus settings with output feature flags
 */

import { useState, useEffect, useMemo } from 'react';
import PeriodTimeSlider from './PeriodTimeSlider.jsx';
import EntitySelectorTable from './EntitySelectorTable.jsx';

const UG_COLS = [
    { key: 'group_name', label: 'Group', align: 'left', muted: false },
    { key: 'capcode', label: 'Pager #', align: 'left', muted: true },
];
const UT_COLS = [
    { key: 'username', label: 'Username', align: 'left', muted: false },
    { key: 'display_name', label: 'Display', align: 'left', muted: false },
    { key: 'phone', label: 'Phone #', align: 'left', muted: true },
];
const UE_COLS = [
    { key: 'username', label: 'Username', align: 'left', muted: false },
    { key: 'display_name', label: 'Display', align: 'left', muted: false },
    { key: 'email', label: 'Email', align: 'left', muted: true },
];
const CT_COLS = [
    { key: 'contact_first_name', label: 'First Name', align: 'left', muted: false },
    { key: 'contact_last_name', label: 'Last Name', align: 'left', muted: false },
    { key: 'contact_phone', label: 'Phone #', align: 'left', muted: true },
];
const CE_COLS = [
    { key: 'contact_first_name', label: 'First Name', align: 'left', muted: false },
    { key: 'contact_last_name', label: 'Last Name', align: 'left', muted: false },
    { key: 'contact_email', label: 'Email', align: 'left', muted: true },
];

// All output type definitions (used for save payload & edit hydration)
const OUTPUT_CHANNELS = [
    { id: 3,  label: 'PalCare Mobile',    flag: 'app_active',             hasRepeat: true,  entityType: 'group' },
    { id: 4,  label: 'Text Messaging',    flag: 'text_messaging_active',  hasRepeat: true,  entityType: 'user' },
    { id: 5,  label: 'Voice Calls',       flag: 'voice_active',           hasRepeat: true,  entityType: 'user' },
    { id: 6,  label: 'Email',             flag: 'email_active',           hasRepeat: true,  entityType: 'user' },
    { id: 7,  label: 'Pager',             flag: 'pager_active',           hasRepeat: true,  entityType: 'group' },
    { id: 9,  label: 'Offsite Monitoring', flag: 'offsite_active',        hasRepeat: false, entityType: null },
    { id: 17, label: 'Text Messaging',    flag: 'text_messaging_active',  hasRepeat: true,  entityType: 'contact' },
    { id: 18, label: 'Voice Calls',       flag: 'voice_active',           hasRepeat: true,  entityType: 'contact' },
    { id: 19, label: 'Email',             flag: 'email_active',           hasRepeat: true,  entityType: 'contact' },
];

// Paired outputs: user output ID → contact output ID
// These share a single toggle + repeat, but have separate entity tables.
const PAIRED_CHANNELS = { 4: 17, 5: 18, 6: 19 };

// The "primary" channels rendered in the UI loop (contacts are rendered inline under their pair)
const DISPLAY_CHANNELS = OUTPUT_CHANNELS.filter((ch) => !Object.values(PAIRED_CHANNELS).includes(ch.id));

const EscalationDrawer = ({
    open,
    onClose,
    onSave,
    onDelete,
    saving,
    editTarget,
    escMax = 3600,
    userGroups = [],
    users = [],
    emergencyContacts = [],
    campusSettings = {},
}) => {
    const isEdit = !!editTarget;

    const [escName, setEscName] = useState('');
    const [periodStart, setPeriodStart] = useState(0);
    const [periodEnd, setPeriodEnd] = useState(escMax);
    const [errors, setErrors] = useState({});
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    // Output channel state: { [outputTypeId]: { enabled, repeat, entityIds } }
    const [channels, setChannels] = useState({});

    // Initialize channels
    useEffect(() => {
        if (!open) return;

        if (editTarget) {
            setEscName(editTarget.escalation_name || '');
            setPeriodStart(editTarget.period_start ?? 0);
            setPeriodEnd(editTarget.period_end ?? escMax);

            // Build channel state from existing outputs
            const ch = {};
            for (const def of OUTPUT_CHANNELS) {
                const existing = (editTarget.outputs || []).find((o) => o.output_type_id === def.id);
                if (existing) {
                    const entIds = (existing.entities || []).map((e) => String(e.reference));
                    ch[def.id] = {
                        enabled: !!existing.enabled,
                        repeat: existing.repeat_interval ? Math.floor(existing.repeat_interval / 60) : 0,
                        entityIds: entIds,
                    };
                } else {
                    ch[def.id] = { enabled: false, repeat: 0, entityIds: [] };
                }
            }

            // Sync paired channels: if either side is enabled, both should be enabled
            for (const [primaryId, contactId] of Object.entries(PAIRED_CHANNELS)) {
                const pId = parseInt(primaryId);
                const pCh = ch[pId];
                const cCh = ch[contactId];
                if (pCh.enabled || cCh.enabled) {
                    pCh.enabled = true;
                    cCh.enabled = true;
                    // Use the primary's repeat as the shared repeat
                    cCh.repeat = pCh.repeat;
                }
            }

            setChannels(ch);
        } else {
            setEscName('');
            setPeriodStart(0);
            setPeriodEnd(escMax);

            // Default all channels disabled
            const ch = {};
            for (const def of OUTPUT_CHANNELS) {
                ch[def.id] = { enabled: false, repeat: 0, entityIds: [] };
            }
            setChannels(ch);
        }
        setErrors({});
        setConfirmingDelete(false);
    }, [open, editTarget, escMax]);

    const namePattern = /^[a-zA-Z0-9_ \-]+$/;

    const validate = () => {
        const errs = {};
        if (!escName.trim()) errs.name = 'Name is required.';
        else if (!namePattern.test(escName.trim())) errs.name = 'Only letters, numbers, spaces, dashes, and underscores are allowed.';
        // Block enabling a recipient-based output with no recipients (defense-in-depth;
        // the Save button is also disabled live via isFormValid).
        if (misconfiguredOutputs.length) {
            errs.outputs = `Add at least one recipient for: ${misconfiguredOutputs.join(', ')}.`;
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // Enabled recipient-based output channels must have at least one recipient.
    // Paired channels (Text/Voice/Email) are satisfied if EITHER the user list or
    // the emergency-contact list has a selection — mirrors OutputList's warning +
    // Enabled/Disabled logic. Offsite (no entities) is exempt.
    const OUTPUT_LABEL_BY_ID = { 3: 'PalCare Mobile', 4: 'Text Messaging', 5: 'Voice Calls', 6: 'Email', 7: 'Pager' };
    const misconfiguredOutputs = useMemo(() => {
        const missing = [];
        DISPLAY_CHANNELS.forEach((def) => {
            if (!campusSettings[def.flag]) return;   // channel not visible for this campus
            if (!def.entityType) return;             // offsite — no recipients required
            const ch = channels[def.id];
            if (!ch?.enabled) return;
            const contactId = PAIRED_CHANNELS[def.id];
            const primaryHas = (ch.entityIds || []).length > 0;
            const contactHas = contactId ? ((channels[contactId]?.entityIds || []).length > 0) : false;
            if (!primaryHas && !contactHas) missing.push(OUTPUT_LABEL_BY_ID[def.id] || def.label);
        });
        return missing;
    }, [channels, campusSettings]);

    // Live form validity for button state
    const isFormValid = useMemo(() => {
        const name = escName.trim();
        if (!name || !namePattern.test(name)) return false;
        // At least one output channel must be enabled...
        if (!Object.values(channels).some((ch) => ch.enabled)) return false;
        // ...and every enabled recipient-based output must have a recipient.
        return misconfiguredOutputs.length === 0;
    }, [escName, channels, misconfiguredOutputs]);

    const updateChannel = (id, field, value) => {
        setChannels((prev) => {
            const next = { ...prev, [id]: { ...prev[id], [field]: value } };

            // If this is a paired primary channel, sync enabled/repeat to its contact pair
            const contactId = PAIRED_CHANNELS[id];
            if (contactId && (field === 'enabled' || field === 'repeat')) {
                next[contactId] = { ...next[contactId], [field]: value };
            }

            return next;
        });
    };

    const handleSave = () => {
        if (!validate()) return;

        const outputs = OUTPUT_CHANNELS.map((def) => {
            const ch = channels[def.id] || {};
            const existingOutput = isEdit
                ? (editTarget.outputs || []).find((o) => o.output_type_id === def.id)
                : null;

            return {
                ...(existingOutput?.output_id ? { output_id: existingOutput.output_id } : {}),
                output_type_id: def.id,
                enabled: ch.enabled ? true : 0,
                repeat_interval: ch.enabled && def.hasRepeat ? (ch.repeat || 0) * 60 : 0,
                entities: ch.enabled && def.entityType ? ch.entityIds : [],
            };
        });

        onSave({
            escalation_name: escName.trim(),
            period_start: periodStart,
            period_end: periodEnd,
            outputs,
        });
    };

    // Determine which display channels are available based on campus settings
    const visibleChannels = DISPLAY_CHANNELS.filter((ch) => !!campusSettings[ch.flag]);

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`} style={{ maxWidth: '520px' }}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">
                        {isEdit ? 'Edit Escalation' : 'Add Escalation'}
                    </h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {/* Name */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Name <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.name ? ' is-invalid' : ''}`}
                            type="text"
                            value={escName}
                            placeholder="Enter escalation name"
                            onChange={(e) => { setEscName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }}
                        />
                        {errors.name ? <p className="dp-drawer__error">{errors.name}</p> : null}
                    </div>

                    {/* Period slider */}
                    <div style={{ marginTop: 'var(--space-3, 0.75rem)' }}>
                        <label className="dp-drawer__label" style={{ marginBottom: 'var(--space-2, 0.5rem)' }}>
                            Escalation Period
                        </label>
                        <PeriodTimeSlider
                            start={periodStart}
                            end={periodEnd}
                            setStart={setPeriodStart}
                            setEnd={setPeriodEnd}
                            minTime={0}
                            maxTime={escMax}
                        />
                    </div>

                    {/* Output channels */}
                    <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', margin: 'var(--space-4, 1rem) 0 0', paddingTop: 'var(--space-3, 0.75rem)' }}>
                        <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                            Output Channels
                        </h4>
                    </div>

                    {misconfiguredOutputs.length ? (
                        <p className="dp-drawer__error" style={{ marginBottom: 'var(--space-2, 0.5rem)' }}>
                            Add at least one recipient for: {misconfiguredOutputs.join(', ')}.
                        </p>
                    ) : null}

                    {visibleChannels.map((def) => {
                        const ch = channels[def.id] || {};
                        const contactId = PAIRED_CHANNELS[def.id];
                        const contactCh = contactId ? (channels[contactId] || {}) : null;

                        return (
                            <div key={def.id} style={{ marginBottom: 'var(--space-3, 0.75rem)', paddingBottom: 'var(--space-3, 0.75rem)', borderBottom: '1px solid var(--color-border-light, #f0f0f0)' }}>
                                {/* Toggle row */}
                                <div className="cf-output-toggle-row" style={{ borderBottom: 'none', padding: '0.5rem 0' }}>
                                    <span className="cf-output-toggle-label">{def.label}</span>
                                    <label className="dp-toggle">
                                        <input
                                            type="checkbox"
                                            checked={!!ch.enabled}
                                            onChange={(e) => updateChannel(def.id, 'enabled', e.target.checked)}
                                        />
                                        <span className="dp-toggle__track" />
                                    </label>
                                </div>

                                {/* Repeat interval (when enabled and has repeat) */}
                                {ch.enabled && def.hasRepeat ? (
                                    <div className="cf-output-repeat-input">
                                        <label>Repeat every</label>
                                        <input
                                            className="dp-drawer__input"
                                            type="number"
                                            min="0"
                                            value={ch.repeat || 0}
                                            onChange={(e) => updateChannel(def.id, 'repeat', parseInt(e.target.value) || 0)}
                                        />
                                        <span>min</span>
                                    </div>
                                ) : null}

                                {/* Entity selector — Groups (PalCare Mobile / Pager) */}
                                {ch.enabled && def.entityType === 'group' ? (
                                    <EntitySelectorTable
                                        title={"User Groups"}
                                        data={userGroups}
                                        columns={UG_COLS}
                                        selectedIds={ch.entityIds || []}
                                        onSelectionChange={(ids) => updateChannel(def.id, 'entityIds', ids)}
                                        searchKeys={['group_name', 'capcode']}
                                        idKey="group_id"
                                    />
                                ) : null}

                                {/* Entity selector — Users (Text/Voice/Email) */}
                                {ch.enabled && def.entityType === 'user' ? (
                                    <>
                                        <EntitySelectorTable
                                            title={"Users"}
                                            data={users}
                                            columns={def.id === 6 ? UE_COLS : UT_COLS}
                                            selectedIds={ch.entityIds || []}
                                            onSelectionChange={(ids) => updateChannel(def.id, 'entityIds', ids)}
                                            searchKeys={['username', 'display_name', 'phone', 'email']}
                                            idKey="user_id"
                                        />
                                    </>
                                ) : null}

                                {/* Entity selector — Emergency Contacts (paired under same toggle) */}
                                {ch.enabled && contactId && contactCh ? (
                                    <>
                                        <EntitySelectorTable
                                            title={"Emergency Contacts"}
                                            data={emergencyContacts}
                                            columns={contactId === 19 ? CE_COLS : CT_COLS}
                                            selectedIds={contactCh.entityIds || []}
                                            onSelectionChange={(ids) => updateChannel(contactId, 'entityIds', ids)}
                                            searchKeys={['contact_first_name', 'contact_last_name', 'contact_phone', 'contact_email']}
                                            idKey="monitoring_contact_id"
                                        />
                                    </>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                <div className="dp-drawer__footer" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                    {confirmingDelete ? (
                        <div className="cf-inline-confirm">
                            <p className="cf-inline-confirm__message">
                                Are you sure you want to delete this escalation and all its outputs?
                            </p>
                            <div className="cf-inline-confirm__actions">
                                <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmingDelete(false)} disabled={saving}>
                                    No, Keep It
                                </button>
                                <button className="dp-btn dp-btn--danger" onClick={onDelete} disabled={saving}>
                                    {saving ? 'Deleting...' : 'Yes, Delete'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            {isEdit && onDelete ? (
                                <button className="dp-btn dp-btn--danger" onClick={() => setConfirmingDelete(true)} disabled={saving}>
                                    Delete
                                </button>
                            ) : (
                                <span />
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                                <button className="dp-btn dp-btn--primary" disabled={saving || !isFormValid} onClick={handleSave}>
                                    {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Escalation')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default EscalationDrawer;
